import { supabase } from '../../shared/supabase/supabaseClient';
import type { ReportsQueryScope } from '../../reports/domain/reportsQueryScope';

export type PromotionPerformanceRow = {
  promotionId: string | null;
  promotionLabel: string;
  usesCount: number;
  discountTotal: number;
};

export type PromotionPerformanceSnapshot = {
  topPromotions: PromotionPerformanceRow[];
  salesWithDiscountCount: number;
  salesWithoutDiscountCount: number;
  salesWithDiscountTotal: number;
  salesWithoutDiscountTotal: number;
};

/** Código estable para Sentry / mapa UI. */
export const PROMOTION_PERFORMANCE_UNAVAILABLE_CODE = 'promotion_performance_unavailable' as const;

export class PromotionPerformanceUnavailableError extends Error {
  readonly code = PROMOTION_PERFORMANCE_UNAVAILABLE_CODE;

  constructor(message = 'Las métricas de uso de promos todavía no están disponibles.') {
    super(message);
    this.name = 'PromotionPerformanceUnavailableError';
  }
}

function asNumber(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Map RPC jsonb → snapshot TS (snake_case → camelCase). */
export function mapPromotionPerformanceRpc(raw: unknown): PromotionPerformanceSnapshot {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new PromotionPerformanceUnavailableError();
  }
  const o = raw as Record<string, unknown>;
  const topRaw = o.top_promotions;
  const topPromotions: PromotionPerformanceRow[] = Array.isArray(topRaw)
    ? topRaw.map((row) => {
        const r = (row ?? {}) as Record<string, unknown>;
        const id = r.promotion_id != null ? String(r.promotion_id) : null;
        return {
          promotionId: id && id.trim() ? id : null,
          promotionLabel: String(r.promotion_label ?? 'Promo'),
          usesCount: Math.floor(asNumber(r.uses_count)),
          discountTotal: asNumber(r.discount_total),
        };
      })
    : [];

  return {
    topPromotions,
    salesWithDiscountCount: Math.floor(asNumber(o.sales_with_discount_count)),
    salesWithoutDiscountCount: Math.floor(asNumber(o.sales_without_discount_count)),
    salesWithDiscountTotal: asNumber(o.sales_with_discount_total),
    salesWithoutDiscountTotal: asNumber(o.sales_without_discount_total),
  };
}

/**
 * KPI promo por sucursal + rango — `pos.get_promotion_performance_by_date_range`.
 * `scope` reservado (cashier filter futuro); hoy se ignora.
 */
export async function getPromotionPerformanceByDateRange(
  branchId: string,
  fromIso: string,
  toIso: string,
  _scope?: ReportsQueryScope | null
): Promise<PromotionPerformanceSnapshot> {
  const branch = branchId.trim();
  if (!branch) throw new PromotionPerformanceUnavailableError('Falta la sucursal.');

  const { data, error } = await supabase.schema('pos').rpc('get_promotion_performance_by_date_range', {
    p_branch_id: branch,
    p_from: fromIso,
    p_to: toIso,
  });

  if (error) {
    throw new PromotionPerformanceUnavailableError(
      error.message?.trim() || 'No se pudieron cargar las métricas de promos.'
    );
  }

  return mapPromotionPerformanceRpc(data);
}
